import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  switchMap,
} from 'rxjs';
import { UnitService } from '../../services/unit.service';
import {
  SearchableClient,
  SearchableDevice,
  UnitResponseModel,
} from '../../units.model';

type FilterOperator = 'disabled' | 'gt' | 'eq' | 'lt';
type SearchTab = 'units' | 'devices' | 'clients';

const TAB_STORAGE_KEY = 'unit-search:tab';
const DEBOUNCE_MS = 500;

interface AdvancedFilter {
  operator: FilterOperator;
  value: number;
}

interface QuickFilters {
  hasRobots: boolean;
  hasUnusedRobots: boolean;
  hasClients: boolean;
}

interface AdvancedFilters {
  totalRobots: AdvancedFilter;
  robots: AdvancedFilter;
  clients: AdvancedFilter;
  users: AdvancedFilter;
}

@Component({
  selector: 'app-unit-search',
  imports: [MatIcon, MatTooltip, FormsModule],
  templateUrl: './unit-search.component.html',
  styleUrl: './unit-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitSearchComponent {
  private readonly _unitService = inject(UnitService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  public readonly units = input.required<UnitResponseModel[]>();
  public readonly canReadDevices = input<boolean>(true);
  public readonly canReadClients = input<boolean>(true);

  public readonly matchingUnitIdsChange = output<Set<string> | null>();
  public readonly focusUnit = output<string>();
  public readonly navigateToUnit = output<string>();
  public readonly navigateToDevice = output<{ unitId: string; category: string | null }>();
  public readonly navigateToClient = output<{ unitId: string; clientId: string }>();
  public readonly close = output<void>();

  protected readonly searchText = signal('');
  protected readonly advancedExpanded = signal(false);
  /** The tab the user picked, or the one restored from the session. */
  private readonly _selectedTab = signal<SearchTab>(this._restoreTab());

  /**
   * The tab on show: the picked one, unless the user may not read it. A tab
   * restored from the session may date from another role, and opening it
   * would search devices or clients this user cannot read. Derived instead of
   * reset, because the permissions behind the inputs arrive after this
   * component does, and a reset would lose a tab the user may well open.
   */
  protected readonly activeTab = computed<SearchTab>(() => {
    const tab = this._selectedTab();
    if (tab === 'devices' && !this.canReadDevices()) return 'units';
    if (tab === 'clients' && !this.canReadClients()) return 'units';
    return tab;
  });

  // Server-driven search state for devices and clients
  protected readonly devices = signal<SearchableDevice[]>([]);
  protected readonly clients = signal<SearchableClient[]>([]);
  protected readonly searchDataLoading = signal(false);
  protected readonly showSpinner = signal(false);
  protected readonly totalDeviceCount = signal<number | null>(null);
  protected readonly totalClientCount = signal<number | null>(null);
  /** Tracks the query text that produced the current results. */
  protected readonly lastSearchedText = signal('');

  protected readonly quickFilters = signal<QuickFilters>({
    hasRobots: false,
    hasUnusedRobots: false,
    hasClients: false,
  });

  protected readonly advancedFilters = signal<AdvancedFilters>({
    totalRobots: { operator: 'disabled', value: 0 },
    robots: { operator: 'disabled', value: 0 },
    clients: { operator: 'disabled', value: 0 },
    users: { operator: 'disabled', value: 0 },
  });

  protected readonly isFilterActive = computed(() => {
    const text = this.searchText().trim();
    const qf = this.quickFilters();
    const af = this.advancedFilters();

    return (
      text.length > 0 ||
      qf.hasRobots ||
      qf.hasUnusedRobots ||
      qf.hasClients ||
      af.totalRobots.operator !== 'disabled' ||
      af.robots.operator !== 'disabled' ||
      af.clients.operator !== 'disabled' ||
      af.users.operator !== 'disabled'
    );
  });

  protected readonly filteredUnits = computed(() => {
    const units = this.units();
    if (!this.isFilterActive()) {
      return units;
    }

    const text = this.searchText().trim().toLowerCase();
    const qf = this.quickFilters();
    const af = this.advancedFilters();

    const filtered = units.filter((unit) => {
      const stats = unit.stats ?? {
        totalRobots: 0,
        unassignedRobots: 0,
        clientCount: 0,
        userCount: 0,
      };

      // Name search
      if (text && !unit.name.toLowerCase().includes(text)) {
        return false;
      }

      // Quick filters (AND)
      if (qf.hasRobots && stats.totalRobots <= 0) {
        return false;
      }
      if (qf.hasUnusedRobots && stats.unassignedRobots <= 0) {
        return false;
      }
      if (qf.hasClients && stats.clientCount <= 0) {
        return false;
      }
      // Advanced filters (AND)
      if (!this._matchAdvancedFilter(af.totalRobots, stats.totalRobots)) {
        return false;
      }
      if (!this._matchAdvancedFilter(af.robots, stats.unassignedRobots)) {
        return false;
      }
      if (!this._matchAdvancedFilter(af.clients, stats.clientCount)) {
        return false;
      }
      if (!this._matchAdvancedFilter(af.users, stats.userCount)) {
        return false;
      }

      return true;
    });

    // Sort alphabetically by name
    return filtered.sort((a, b) =>
      a.name.localeCompare(b.name, 'nl', { sensitivity: 'base' })
    );
  });

  /** Map of unit ID → unit name for showing parent unit in device/client results */
  protected readonly unitNameMap = computed(() => {
    const map = new Map<string, string>();
    for (const unit of this.units()) {
      map.set(unit._id, unit.name);
    }
    return map;
  });

  protected readonly matchingIds = computed<Set<string> | null>(() => {
    if (!this.isFilterActive()) {
      return null;
    }
    return new Set(this.filteredUnits().map((u) => u._id));
  });

  constructor() {
    effect(() => {
      // Only emit matching IDs when on the units tab
      if (this.activeTab() === 'units') {
        this.matchingUnitIdsChange.emit(this.matchingIds());
      } else {
        this.matchingUnitIdsChange.emit(null);
      }
    });

    // Delay spinner visibility: show after 250ms, linger 200ms after
    // loading finishes so the CSS fade-out is perceptible.
    effect((onCleanup) => {
      const loading = this.searchDataLoading();

      if (loading) {
        const id = setTimeout(() => this.showSpinner.set(true), 150);
        onCleanup(() => clearTimeout(id));
      } else if (this.showSpinner()) {
        const id = setTimeout(() => this.showSpinner.set(false), 200);
        onCleanup(() => clearTimeout(id));
      }
    });

    this._setupSearchSubscription();

    afterNextRender(() => {
      this.focusInput();
    });
  }

  public focusInput(): void {
    this._searchInput()?.nativeElement.focus();
  }

  public appendToSearch(char: string): void {
    this.searchText.update((text) => text + char);
    this.focusInput();
  }

  public appendToSearchIfBlurred(char: string): void {
    if (document.activeElement !== this._searchInput()?.nativeElement) {
      this.appendToSearch(char);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.isFilterActive()) {
      this.clearAll();
    } else {
      this.close.emit();
    }
  }

  protected setActiveTab(tab: SearchTab): void {
    this._selectedTab.set(tab);
    this._persistTab(tab);
  }

  protected toggleQuickFilter(key: keyof QuickFilters): void {
    const isActive = this.quickFilters()[key];

    // Radio behavior: only one quick filter at a time
    const reset: QuickFilters = {
      hasRobots: false,
      hasUnusedRobots: false,
      hasClients: false,
    };

    this.quickFilters.set({
      ...reset,
      [key]: !isActive,
    });

    // If turning on a quick filter, disable all advanced filters
    if (!isActive) {
      this._resetAdvancedFilters();
    }
  }

  protected updateAdvancedOperator(
    key: keyof AdvancedFilters,
    operator: FilterOperator
  ): void {
    // If enabling an advanced filter, disable all quick filters
    if (operator !== 'disabled') {
      this._resetQuickFilters();
    }

    this.advancedFilters.update((current) => ({
      ...current,
      [key]: { ...current[key], operator },
    }));
  }

  protected updateAdvancedValue(key: keyof AdvancedFilters, value: number): void {
    this.advancedFilters.update((current) => ({
      ...current,
      [key]: { ...current[key], value: Math.max(0, value) },
    }));
  }

  protected clearAll(): void {
    this.searchText.set('');
    this.quickFilters.set({
      hasRobots: false,
      hasUnusedRobots: false,
      hasClients: false,
    });
    this.advancedFilters.set({
      totalRobots: { operator: 'disabled', value: 0 },
      robots: { operator: 'disabled', value: 0 },
      clients: { operator: 'disabled', value: 0 },
      users: { operator: 'disabled', value: 0 },
    });
  }

  protected onFocusUnit(unitId: string): void {
    this.focusUnit.emit(unitId);
  }

  protected onNavigateToUnit(event: Event, unitId: string): void {
    event.stopPropagation();
    this.navigateToUnit.emit(unitId);
  }

  protected onNavigateToDevice(event: Event, device: SearchableDevice): void {
    event.stopPropagation();
    this.navigateToDevice.emit({ unitId: device.unitId, category: device.category });
  }

  protected onNavigateToClient(event: Event, client: SearchableClient): void {
    event.stopPropagation();
    this.navigateToClient.emit({ unitId: client.unitId, clientId: client._id });
  }

  private _setupSearchSubscription(): void {
    const searchText$ = toObservable(this.searchText);
    const activeTab$ = toObservable(this.activeTab);

    // Fetch total count when switching to a devices/clients tab
    activeTab$
      .pipe(
        filter((tab): tab is 'devices' | 'clients' => tab === 'devices' || tab === 'clients'),
        switchMap((tab) => {
          if (tab === 'devices') {
            return this._unitService.searchDevices().pipe(
              map((r) => ({ tab, total: r.total } as const))
            );
          }
          return this._unitService.searchClients().pipe(
            map((r) => ({ tab, total: r.total } as const))
          );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(({ tab, total }) => {
        if (tab === 'devices') {
          this.totalDeviceCount.set(total);
        } else {
          this.totalClientCount.set(total);
        }
      });

    // Debounced search — only fires for non-empty queries
    combineLatest([searchText$, activeTab$])
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(
          ([prevText, prevTab], [currText, currTab]) =>
            prevText === currText && prevTab === currTab
        ),
        filter(([, tab]) => tab === 'devices' || tab === 'clients'),
        switchMap(([text, tab]) => {
          const query = text.trim();

          if (!query) {
            // Clear results immediately, no request needed
            this.devices.set([]);
            this.clients.set([]);
            this.searchDataLoading.set(false);
            this.lastSearchedText.set('');
            return EMPTY;
          }

          this.searchDataLoading.set(true);

          if (tab === 'devices') {
            return this._unitService.searchDevices(query).pipe(
              map((response) => ({ tab, response } as const))
            );
          } else {
            return this._unitService.searchClients(query).pipe(
              map((response) => ({ tab, response } as const))
            );
          }
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: ({ tab, response }) => {
          if (tab === 'devices') {
            this.devices.set(response.results as SearchableDevice[]);
            this.totalDeviceCount.set(response.total);
          } else {
            this.clients.set(response.results as SearchableClient[]);
            this.totalClientCount.set(response.total);
          }
          this.lastSearchedText.set(this.searchText().trim());
          this.searchDataLoading.set(false);
        },
        error: () => {
          this.searchDataLoading.set(false);
        },
      });
  }

  private _resetQuickFilters(): void {
    this.quickFilters.set({
      hasRobots: false,
      hasUnusedRobots: false,
      hasClients: false,
    });
  }

  private _resetAdvancedFilters(): void {
    this.advancedFilters.set({
      totalRobots: { operator: 'disabled', value: 0 },
      robots: { operator: 'disabled', value: 0 },
      clients: { operator: 'disabled', value: 0 },
      users: { operator: 'disabled', value: 0 },
    });
  }

  private _matchAdvancedFilter(filter: AdvancedFilter, value: number): boolean {
    switch (filter.operator) {
      case 'disabled':
        return true;
      case 'gt':
        return value > filter.value;
      case 'eq':
        return value === filter.value;
      case 'lt':
        return value < filter.value;
      default:
        return true;
    }
  }

  private _restoreTab(): SearchTab {
    try {
      const stored = sessionStorage.getItem(TAB_STORAGE_KEY);
      if (stored === 'units' || stored === 'devices' || stored === 'clients') {
        return stored;
      }
    } catch { /* sessionStorage unavailable */ }
    return 'units';
  }

  private _persistTab(tab: SearchTab): void {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch { /* sessionStorage unavailable */ }
  }
}
